import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Col } from '@sm/wds-react';
import { IconArrowRight } from '@sm/wds-icons';

import { PACKAGES, MONTHLY_PACKAGES, TEAMS_PACKAGES } from '~pricing/lib/constants';
import { PackageListPropTypes } from '~pricing/lib/proptypes';

import './summary-footer.scss';

const FaqQuestion = ({ children }) => (
  <Typography component="dt" variant="cardTitle" className="wds-p-t-3">
    {children}
  </Typography>
);

FaqQuestion.propTypes = {
  children: PropTypes.string.isRequired,
};

const FaqAnswer = ({ children }) => (
  <Typography component="dd" variant="body" className="wds-p-t-2" data-testid="SummaryFooter__FaqAnswer">
    {children}
  </Typography>
);

FaqAnswer.propTypes = {
  children: PropTypes.node.isRequired,
};

const SummaryFooter = ({ packages }) => (
  <footer className="wds-grid" key="SummaryFooter" data-testid="SummaryFooter__PricingPackagesFooter">
    <ul className="sm-summary-footer__footnotes">
      <li className="sm-summary-footer__footnotes-item">
        {`*Displayed pricing represents a 20% or more savings per user per month `}
        {`compared to individual Advantage and Premier plans`}
      </li>
      <li className="sm-summary-footer__footnotes-item">
        **Add-on features available with Enterprise plan must be purchased separately
      </li>
    </ul>
    <Typography variant="cardTitle" align="center">
      <a href="/pricing/details/">
        {`SEE ALL PLAN FEATURES `}
        <IconArrowRight />
      </a>
    </Typography>
    <Typography
      component="h3"
      variant="sectionTitle"
      align="center"
      className="sm-summary-footer__faq-header wds-p-t-6 wds-p-b-6 wds-m-t-6"
    >
      More about plans and pricing
    </Typography>
    <dl className="wds-grid__row">
      <Col sm={12} md={6}>
        <FaqQuestion>How much does SurveyMonkey cost?</FaqQuestion>
        {packages.map(({ package: packageName, details, skuCost }) => {
          // TODO: Make a better component out of this with filtering to remove strange
          //       strange one off business logic like this
          if (packageName === PACKAGES.Enterprise) {
            return null;
          }

          if (packageName === PACKAGES.Basic) {
            return <FaqAnswer key={packageName}>{`The ${details.displayName} plan is FREE`}</FaqAnswer>;
          }

          const coreSeatCost = skuCost.find(sku => sku.skuType === 'SUBSCRIPTION_CORE_SEAT');
          let cost = coreSeatCost.cost / 100;
          let term = '';
          if (TEAMS_PACKAGES.includes(packageName)) {
            term += ', per user';
          }

          if (!MONTHLY_PACKAGES.includes(packageName)) {
            cost /= 12;
            term += ', billed annually';
          }

          return (
            <FaqAnswer key={`plan-price-${packageName}`}>
              {`The ${details.displayName} plan costs $${cost} a month${term}.`}
            </FaqAnswer>
          );
        })}
      </Col>
      <Col sm={12} md={6}>
        <FaqQuestion>Do you offer special pricing for students or educations?</FaqQuestion>
        <FaqAnswer>
          {`Yes! We offer discounted pricing for students and educators. `}
          <a href="#">
            {`See pricing `}
            <IconArrowRight />
          </a>
        </FaqAnswer>
        <FaqQuestion>Do you offer discounts for teams?</FaqQuestion>
        <FaqAnswer>
          {`We offer discounts when you sign up for team plans that include 3 or `}
          {`more people. With Team Advantage and Team Premier plans, you’ll save `}
          {`20% or more over individual plans and get access to additional `}
          {`collaboration features. `}
          <a href="#">
            {`See pricing `}
            <IconArrowRight />
          </a>
        </FaqAnswer>
        <FaqQuestion>Do you offer plans for corporate needs?</FaqQuestion>
        <FaqAnswer>
          {`Our Enterprise plans offer HIPAA compliance, advanced security, and `}
          {`admin features for increased governance and control. `}
          <a href="#">
            {`Contact sales `}
            <IconArrowRight />
          </a>
        </FaqAnswer>
      </Col>
    </dl>
  </footer>
);

SummaryFooter.propTypes = {
  packages: PackageListPropTypes.isRequired,
};

export default SummaryFooter;
